<template>
  <v-dialog v-model="dialog" max-width="60%">
    <template v-slot:activator="{ on }">
      <slot name="button" :on="on">
        <v-btn outlined rounded small v-on="on" color="primary">Add New</v-btn>
      </slot>
    </template>
    <v-card>
      <v-card-title class="headline">
        <slot name="title">
          <div>Create a New Project</div>
        </slot>
        <v-spacer />
        <v-btn icon @click="close"><v-icon>close</v-icon></v-btn>
      </v-card-title>
      <v-card-text>
        <v-form v-model="$v.valid" @submit.prevent="submit">
          <v-text-field
            v-model.trim="$v.formdata.forgeID.$model"
            :error-messages="forgeIDErrors"
            label="Forge ID Name or Title"
            counter="16"
            hint="FN0xxx"
            prepend-icon="fas fa-fingerprint"
            @input="$v.formdata.forgeID.$touch()"
            @blur="$v.formdata.forgeID.$touch()"
          />
          <v-text-field
            v-model.trim="$v.formdata.name.$model"
            :error-messages="nameErrors"
            label="Project Name or Title"
            counter="124"
            hint="Something that briefly describes this Project"
            prepend-icon="fas fa-id-card"
            @input="$v.formdata.name.$touch()"
            @blur="$v.formdata.name.$touch()"
          />
          <v-autocomplete
            v-model="$v.formdata.company_id.$model"
            :error-messages="companyErrors"
            label="Company Selection"
            :items="companies"
            item-value="id"
            item-text="name"
            hint="Select the client or company for this job"
            prepend-icon="add_box"
            append-icon="fas fa-building"
            @click:prepend="showCompany = !showCompany"
            @input="$v.formdata.company_id.$touch()"
            @blur="$v.formdata.company_id.$touch()"
          />
          <v-row justify="center">
            <v-col sm="8" cols="8">
              <CompanyFormNew v-show="showCompany" />
            </v-col>
          </v-row>
          <v-autocomplete
            v-model="$v.formdata.industry_id.$model"
            :error-messages="industryErrors"
            label="Industry Selection"
            :loading="industryLoading"
            :items="industries"
            item-value="id"
            item-text="name"
            hint="Select the field or industry the project applies to"
            prepend-icon="add_box"
            @click:prepend="showIndustry = !showIndustry"
            append-icon="fas fa-industry"
            @input="$v.formdata.industry_id.$touch()"
            @blur="$v.formdata.industry_id.$touch()"
          />
          <v-row justify="center">
            <v-col sm="8" cols="8">
              <IndustryFormNew v-show="showIndustry" />
            </v-col>
          </v-row>
          <v-autocomplete
            v-model="$v.formdata.user_id.$model"
            :error-messages="userErrors"
            :loading="userLoading"
            label="Project Owner"
            :items="users"
            item-value="id"
            item-text="display_name"
            hint="Who's running point on this thing"
            prepend-icon="fas fa-users"
            @input="$v.formdata.user_id.$touch()"
            @blur="$v.formdata.user_id.$touch()"
          />
          <v-autocomplete
            v-model="$v.formdata.BDuser_id.$model"
            :error-messages="userErrors"
            :loading="userLoading"
            label="BD Owner"
            :items="users"
            item-value="id"
            item-text="display_name"
            hint="Who's the customer interface?"
            prepend-icon="fas fa-users"
            @input="$v.formdata.BDuser_id.$touch()"
            @blur="$v.formdata.BDuser_id.$touch()"
          />
          <v-checkbox v-model="formdata.commercial" label="Commercial Project" />
          <v-checkbox
            v-model="formdata.invoiceable"
            label="Project Will Have an Invoice"
          />
          <v-checkbox v-model="formdata.grant" label="Grant Funded Project" />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close"><v-icon left>close</v-icon>Close</v-btn>
        <v-btn color="warning" text @click="clear">
          <v-icon left>fas fa-redo</v-icon>
          Clear
        </v-btn>
        <v-btn :disabled="$v.$invalid" text color="green" @click="submit">
          <v-icon left>save</v-icon>
          Submit
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Project from "../../models/Project";
import Company from "../../models/Company";
import Industry from "../../models/Industry";
import User from "../../models/User";
import { validationMixin } from "vuelidate";
import { handleErrors } from "../../services/formErrors";
import CompanyFormNew from "./CompanyFormNew";
import IndustryFormNew from "./IndustryFormNew";
export default {
  name: "ProjectForm",
  components: { CompanyFormNew, IndustryFormNew },
  mixins: [validationMixin],
  validations: Project.validations,
  props: {
    instance: {
      type: Object,
      default: () => {}
    },
    isEdit: {
      type: Boolean,
      required: true,
      default: false
    }
  },
  data() {
    return {
      industryLoading: true,
      stateLoading: true,
      userLoading: true,
      companyLoading: true,
      formdata: new Project(this.instance),
      showCompany: false,
      showIndustry: false,
      dialog: false
    };
  },
  computed: {
    // return error messages for validated fields
    nameErrors() {
      return handleErrors("formdata", "name", this);
    },
    companyErrors() {
      return handleErrors("formdata", "company_id", this);
    },
    userErrors() {
      return handleErrors("formdata", "user_id", this);
    },
    industryErrors() {
      return handleErrors("formdata", "industry_id", this);
    },
    forgeIDErrors() {
      return handleErrors("formdata", "forgeID", this);
    },
    users() {
      return User.query().orderBy('display_name').get();
    },
    industries() {
      return Industry.query().orderBy('name').get();
    },
    companies() {
      return Company.query().orderBy('name').get();
    }
  },
  created() {
    this.initialize()
  },
  methods: {
    initialize() {
      User.$fetch().then(() => (this.userLoading = false));
      Industry.$fetch().then(() => (this.industryLoading = false));
      Company.$fetch().then(() => (this.companyLoading = false));
    },
    close() {
      this.dialog = false;
    },
    submit() {
      this.$v.$touch();
      if (this.isEdit) {
        Project
          .$put(this.instance.id, this.formdata.toUpdatePayload())
          .then(() => this.close())
      } else {
        Project
          .$post(this.formdata.toCreatePayload())
          .then(() => {
              this.clear();
              this.close();
            });
      }
    },
    clear() {
      this.formdata = new Project();
      this.$v.$reset();
    }
  }
};
</script>
